import type { ResourceCard } from "./types";

/**
 * THE ONE PLACE TO EDIT AFTER `npm run devlink:sync`.
 *
 * Maps a `ResourceCard` onto the props of the Webflow component
 * `case-study-card-devlink`. DevLink derives prop names from the component's
 * properties in Webflow Designer, so the exact set is site-specific and cannot
 * be known ahead of the first sync.
 *
 * To reconcile:
 *   1. `npm run devlink:sync`
 *   2. Open `devlink/CaseStudyCardDevlink.d.ts` — it lists every real prop.
 *   3. Rename keys below to match. Leave the right-hand sides alone.
 *
 * `npm run devlink:status` prints a side-by-side of the two after a sync.
 *
 * Keys with an `undefined` value are stripped, not passed. This matters:
 * DevLink components declare defaults as `function Card({ image = "https://…" })`,
 * so passing `image: undefined` would silently render a placeholder image
 * baked in from the Webflow Designer canvas.
 *
 * Reconciled against the sync of 2026-08-08. The component declares exactly
 * five props — `buttonText`, `link`, `resourceTag`, `thumbnail`, `title`.
 *
 * Two things the pre-sync stand-in accepted have no home: `variant`, because
 * the component has no variants, and the `*Visibility` flags, because the
 * component decides for itself.
 *
 * **`description` is deliberately not sent.** The card's paragraph was removed
 * in Designer, so the property no longer exists; passing it would be a prop the
 * component ignores. `ResourceCard.description` is still mapped from the CMS
 * and still indexed — `SEARCHABLE_FIELDS` in `resources.ts` searches it — so a
 * visitor can still find a resource by words that only appear in its summary,
 * even though that summary is never rendered. Deleting the field would quietly
 * shrink what search matches.
 */
export function cardPropsFor(card: ResourceCard): Record<string, unknown> {
  return omitUndefined({
    title: card.title,

    // The eyebrow tag ("E-Book", "Analyst report", …). ALWAYS pass this, never
    // let it fall through to the default: the component renders it
    // unconditionally and its Designer default is the literal string
    // "Whitepaper", so an unset value silently mislabels every card as a
    // whitepaper.
    resourceTag: card.typeLabel,

    // `Types.Asset.Image` is a bare URL string, not an object.
    //
    // `null`, not `undefined`, when the CMS item has no image — deliberately
    // NOT letting the component default apply here. That default is `""`, and
    // React refuses an empty `src`: it drops the attribute and logs "An empty
    // string ("") was passed to the src attribute" once per card, per render.
    // `null` produces the identical DOM (an `<img>` with no `src`) and no
    // warning, which is exactly what React's own message recommends.
    //
    // Either way the thumbnail box still renders empty. Fixing that properly
    // means a default asset or a visibility condition on the Designer property.
    thumbnail: card.image?.url ?? null,

    // Webflow's Link prop shape — `Types.Basic.Link` is `{ href, target?, preload? }`.
    link: { href: card.url },

    // The component's own default is "Learn more"; drop this line to inherit it.
    buttonText: "Read more",
  });
}

function omitUndefined(o: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));
}
